import React from "react";
import PropTypes from "prop-types";
import { Card, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip } from "@mui/material";
import clsx from "clsx";
import { useTheme, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  VolumeNumberFormatString as formatVolume,
  EnergyNumberFormatString as formatEnergy,
  CurrencyNumberFormatString as formatCurrency
} from 'src/utils/numbers';
import { faBolt, faBoltLightning, faChargingStation, faDroplet, faFire, faWater } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  label: {
    marginLeft: theme.spacing(1)
  },
  table: {
    border: 'none'
  },
  tablecell: {
    border: 'none',
    padding: '0px'
  }
}));

function formatDifference(value) {
  if (value === 0) return "0";
  return value > 0 ? `+${value}` : `${value}`;
}

function LastMonthComparisonCard({ consumption, lastMonthData, className, ...rest }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!consumption || !lastMonthData) return null;

  const formatters = {
    volume: formatVolume,
    energy: formatEnergy,
    empty: () => ''
  };

  const rows = [
    {
    field: 'coldwater',
    text: 'Kallvatten',
    formatter: 'volume',
    visible: consumption.coldWaterSensorsExists,
    icon: faDroplet,
    iconColor: "#0289d1"
    },
    {
    field: 'hotwater',
    text: 'Varmvatten',
    formatter: 'volume',
    visible: consumption.hotWaterSensorsExists,
    icon: faDroplet,
    iconColor: "red"
    },
    {
    field: 'electricity',
    text: 'El',
    formatter: 'energy',
    visible: consumption.electicitySensorsExists,
    icon: faBoltLightning,
    iconColor: "yellow"
    },
    {
    field: 'heatenergy',
    text: 'Värmeenergi',
    formatter: 'energy',
    visible: consumption.heatEnergySensorsExists,
    icon: faFire,
    iconColor: "orange"
    },
    {
    field: 'evCharging',
    text: 'Laddning av elbil',
    formatter: 'energy',
    visible: consumption.evChargingSensorsExists,
    icon: faChargingStation,
    iconColor: "green"
    }
  ];

  const getDifference = (field) => (consumption.periodSummary[field].value - lastMonthData[field].value)?.toFixed(2);
  const getColor = (diff) => diff > 0 ? "red" : diff < 0 ? "green" : "textSecondary";

  const ConsumptionRow = ({ children }) => <TableRow>{children}</TableRow>;
  const ConsumptionCell = ({ text, icon, iconColor, align = "left", color }) => (
    <TableCell className={classes.tablecell} align={align}>
      <Typography variant="h4" color={color || 'textSecondary'}>
        {icon && <FontAwesomeIcon icon={icon} color={iconColor} style={{marginRight: 8}}/>}
        {text}
      </Typography>
    </TableCell>
  );

  return (
    <Card className={clsx(classes.root, className)} {...rest}>
      <Box flexGrow={1}>
        <Typography component="h3" gutterBottom variant="overline" color="textSecondary">
          Jämförelse mot föregående månad
        </Typography>

        {!isMobile ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <ConsumptionRow>
                  <ConsumptionCell text="Typ" />
                  <ConsumptionCell text="Denna månad" align="right" />
                  <ConsumptionCell text="Föregående månad" align="right" />
                  <ConsumptionCell text="Skillnad" align="right" />
                </ConsumptionRow>
              </TableHead>
              <TableBody>
                {rows.map(row => row.visible && (
                  <ConsumptionRow key={row.field}>
                    <ConsumptionCell text={row.text} icon={row.icon} iconColor={row.iconColor} />
                    <ConsumptionCell text={formatters[row.formatter](consumption.periodSummary[row.field].value)} align="right" />
                    <ConsumptionCell text={formatters[row.formatter](lastMonthData[row.field].value)} align="right" />
                    <ConsumptionCell text={formatDifference(getDifference(row.field))} align="right" color={getColor(getDifference(row.field))} />
                  </ConsumptionRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {rows.map(row => row.visible && (
              <Box key={row.field} display="flex" flexDirection="column" p={1} borderBottom={`1px solid ${theme.palette.divider}`}>
                <Typography variant="subtitle2" color="textSecondary">{row.icon && <FontAwesomeIcon icon={row.icon} color={row.iconColor} style={{marginRight: 8}}/>}{row.text}</Typography>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body1">{formatters[row.formatter](consumption.periodSummary[row.field].value)}</Typography>
                  <Typography variant="body1">{formatters[row.formatter](lastMonthData[row.field].value)}</Typography>
                  <Typography variant="body1" color={getColor(getDifference(row.field))}>{formatDifference(getDifference(row.field))}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
}

LastMonthComparisonCard.propTypes = {
  className: PropTypes.string,
  consumption: PropTypes.object.isRequired,
  lastMonthData: PropTypes.object.isRequired
};

export default LastMonthComparisonCard;